import { AutocompleteItem } from '@prismamedia/one-components';
import { GetRecipeCategoriesQuery_categories_Category } from '__generated__/queries-recipe';
import { useGetCategories } from 'apollo/queries/categories.recipe.graphql';
import { getFilteredBrandKey } from 'utils/queries/variables';

const getSecondaryLabel = (count: number, displayCount: boolean) =>
  displayCount ? `${count} recette${count > 1 ? 's' : ''}` : undefined;

const spreadCategoryChildren = (
  categories: GetRecipeCategoriesQuery_categories_Category[],
  displayCount: boolean,
  parents: GetRecipeCategoriesQuery_categories_Category[],
  level = -1,
): AutocompleteItem[] =>
  parents
    .reduce(
      (prev, category) => [
        ...prev,
        {
          id: category.id,
          label: category.title,
          level,
          secondaryLabel:
            category.recipeCategoryCount &&
            getSecondaryLabel(category.recipeCategoryCount, displayCount),
          additionnals: { category },
        },
        ...spreadCategoryChildren(
          categories,
          displayCount,
          categories.filter(({ parent }) => parent?.id === category.id),
          level + 1,
        ),
      ],
      [] as AutocompleteItem[],
    )
    .filter((parent) => parent.level !== -1);

export const useFetchCategories = (
  brandKey: string | null,
  displayCount = false,
) => {
  const getCategories = useGetCategories();

  return async (search: string) => {
    const data = await getCategories({
      where: {
        ...getFilteredBrandKey(brandKey),
        title_contains: search || undefined,
        level_gte: 2,
      },
      countWhere: {
        position: 1,
      },
      fetchCount: displayCount,
    });

    if (search) {
      return data?.categories
        ?.filter(({ level }) => level > 2)
        .map(({ id, title, recipeCategoryCount }) => ({
          id,
          label: title,
          secondaryLabel:
            recipeCategoryCount &&
            getSecondaryLabel(recipeCategoryCount, displayCount),
        }));
    }
    const categories = data?.categories || [];

    return spreadCategoryChildren(
      categories,
      displayCount,
      categories.filter(({ level }) => level === 2),
    );
  };
};
